import { Link } from "react-router-dom";
import type { MediaItem } from "@/types/media";
import { typeLabelMap } from "@/utils/media";

interface MediaCardProps {
  item: MediaItem;
  showSynopsis?: boolean;
  rank?: number;
}

export default function MediaCard({ item, showSynopsis = true, rank }: MediaCardProps) {
  return (
    <Link to={`/detail/${item.id}`} className="group flex h-full cursor-pointer flex-col">
      <div className="relative aspect-[3/4] w-full overflow-hidden rounded-lg bg-background-200">
        <img
          src={item.cover}
          alt={`${item.title} 封面`}
          title={`${item.title} - 日韩影视详情`}
          className="h-full w-full object-cover object-top transition-transform duration-700 ease-smooth group-hover:scale-[1.05]"
        />
        <div className="absolute inset-x-0 top-0 flex items-start justify-between gap-2 p-2.5">
          <span className="flex items-center gap-1 rounded-full bg-accent-500 px-2 py-0.5 font-label text-[11px] font-semibold text-primary-950">
            <i className="ri-star-fill text-[10px]" />
            {item.rating.toFixed(1)}
          </span>
          <span className="rounded-full bg-primary-950/75 px-2 py-0.5 text-[11px] text-background-50 backdrop-blur-sm">
            {typeLabelMap[item.type]}
          </span>
        </div>
        {typeof rank === "number" && (
          <span className="absolute bottom-2.5 left-2.5 flex h-7 w-7 items-center justify-center rounded-md bg-background-50/95 font-label text-xs font-bold text-primary-800">
            {String(rank).padStart(2, "0")}
          </span>
        )}
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-primary-950/95 via-primary-950/45 to-transparent px-2.5 pb-2.5 pt-10">
          <p className="flex items-center gap-1.5 truncate text-[11px] text-background-200">
            <span>{item.year}</span>
            <span className="text-background-400">·</span>
            <span>{item.region}</span>
            <span className="text-background-400">·</span>
            <span className="truncate">{item.episodes}</span>
          </p>
        </div>
      </div>

      <div className="mt-3 flex flex-1 flex-col">
        <h3 className="line-clamp-1 text-[15px] font-medium leading-snug text-foreground-950 transition-colors group-hover:text-primary-700">
          {item.title}
        </h3>
        <p className="mt-1 truncate text-xs text-foreground-500">主演：{item.cast.slice(0, 2).join(" / ")}</p>
        {showSynopsis && (
          <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-foreground-500">{item.synopsis}</p>
        )}
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          {item.genres.slice(0, 3).map((genre) => (
            <span
              key={genre}
              className="rounded-md bg-background-100 px-1.5 py-0.5 text-[11px] text-foreground-600"
            >
              {genre}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
}